import React from 'react';

import { Gateway } from 'types/gateway';

import { useCreateStream } from 'streams/hooks/useCreateStream';

import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { Loader } from 'components/Loader/Loader';
import { Text } from 'components/Text/Text';

import { SourceCredentialsForm } from './SourceCredentialsForm';
import { SourceCameraList } from './SourceCameraList';
import { sourceConfigs } from './sources';
import { ServerOption, SourceCamera, SourceType } from './types';

type ImportFromSourceProps = {
  sourceType: SourceType;
  gateway?: Gateway;
  onSuccess: () => void;
};

type ImportState =
  | { step: 'credentials' }
  | { step: 'server-select'; servers: ServerOption[] }
  | { step: 'cameras'; cameras: SourceCamera[] }
  | { step: 'done'; imported: number; failed: number };

export function ImportFromSource({
  sourceType,
  gateway,
  onSuccess,
}: ImportFromSourceProps) {
  const config = sourceConfigs[sourceType];
  const apiBaseUrl = import.meta.env.VITE_API_URL as string;

  const [state, setState] = React.useState<ImportState>({
    step: 'credentials',
  });
  const [credentials, setCredentials] = React.useState<Record<string, string>>(
    {}
  );
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  const [isFetching, setIsFetching] = React.useState(false);
  const [quality, setQuality] = React.useState(config.defaultQuality ?? '');
  const [gatewayId, setGatewayId] = React.useState<string | undefined>(
    gateway?.id
  );
  const [selectedServerId, setSelectedServerId] = React.useState<string | null>(null);
  const [isImporting, setIsImporting] = React.useState(false);
  const [importProgress, setImportProgress] = React.useState<{
    done: number;
    total: number;
  } | null>(null);

  const { mutateAsync: createStream } = useCreateStream();

  const aliveRef = React.useRef(true);
  React.useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  async function handleFetchCameras(creds: Record<string, string>) {
    setCredentials(creds);
    setFetchError(null);
    setIsFetching(true);

    try {
      // If source supports server selection, fetch servers first
      if (config.fetchServers) {
        const servers = await config.fetchServers(creds, apiBaseUrl);
        setState({ step: 'server-select', servers });
      } else {
        const cameras = await config.fetchCameras(creds, apiBaseUrl);
        setState({ step: 'cameras', cameras });
      }
    } catch (err) {
      setFetchError(
        err instanceof Error ? err.message : 'Failed to fetch cameras'
      );
    } finally {
      setIsFetching(false);
    }
  }

  async function handleSelectServer(serverId: string) {
    setSelectedServerId(serverId);
    setFetchError(null);
    setIsFetching(true);

    try {
      const cameras = await config.fetchCameras(
        credentials,
        apiBaseUrl,
        serverId
      );
      setState({ step: 'cameras', cameras });
    } catch (err) {
      setFetchError(
        err instanceof Error ? err.message : 'Failed to fetch cameras'
      );
    } finally {
      setIsFetching(false);
    }
  }

  async function handleImport(cameras: SourceCamera[]) {
    setIsImporting(true);
    setImportProgress({ done: 0, total: cameras.length });

    let imported = 0;
    let failed = 0;

    for (const camera of cameras) {
      if (!aliveRef.current) return;

      try {
        const uri = await config.buildStreamUri(
          camera,
          credentials,
          quality,
          apiBaseUrl
        );
        const name = config.buildStreamName(camera, quality);

        await createStream({
          name,
          source: 'uri_stream',
          stream_type: 'rtsp',
          uri,
          gateway_id: gatewayId,
          insecure_disable_tls_verification:
            config.disableTlsVerification ?? false,
        });

        imported++;
      } catch {
        failed++;
      }

      setImportProgress({ done: imported + failed, total: cameras.length });
    }

    setIsImporting(false);
    setState({ step: 'done', imported, failed });
  }

  return (
    <div className="flex flex-col gap-3">
      {config.docsUrl && (
        <ExternalLink href={config.docsUrl}>
          {config.label} setup guide
        </ExternalLink>
      )}

      {state.step === 'credentials' && (
        <SourceCredentialsForm
          config={config}
          onSubmit={handleFetchCameras}
          isLoading={isFetching}
          error={fetchError}
        />
      )}

      {state.step === 'server-select' && (
        <div className="flex flex-col gap-3">
          <Text size="small" className="text-[var(--ui-fg-muted)]">
            Select a server to fetch cameras from:
          </Text>
          {fetchError && <p className="form-error theme danger">{fetchError}</p>}
          <div className="flex flex-col gap-2">
            {state.servers.map((server) => {
              const isSelected = selectedServerId === server.id;
              return (
                <Card
                  key={server.id}
                  size="small"
                  onClick={() => {
                    if (!isFetching) handleSelectServer(server.id);
                  }}
                  className={
                    isFetching
                      ? isSelected
                        ? 'cursor-wait'
                        : 'opacity-50 pointer-events-none'
                      : ''
                  }
                >
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col gap-0.5 flex-1">
                      <span className="font-medium text-sm">{server.name}</span>
                      {server.description && (
                        <span className="text-xs text-[var(--ui-fg-muted)]">
                          {server.description}
                        </span>
                      )}
                    </div>
                    {isFetching && isSelected && (
                      <Loader text="Loading cameras..." size="xsmall" />
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {state.step === 'cameras' && (
        <SourceCameraList
          cameras={state.cameras}
          config={config}
          quality={quality}
          onQualityChange={setQuality}
          gateway={gateway}
          gatewayId={gatewayId}
          onGatewayIdChange={setGatewayId}
          onImport={handleImport}
          isImporting={isImporting}
          isFetching={isFetching}
          importProgress={importProgress}
        />
      )}

      {state.step === 'done' && (
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            Successfully imported {state.imported} stream
            {state.imported !== 1 ? 's' : ''}.
            {state.failed > 0 && (
              <span className="text-[var(--danger)]">
                {' '}
                {state.failed} failed.
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <Button variant="primary" onClick={onSuccess}>
              Done
            </Button>
            <Button
              variant="tertiary"
              onClick={() => setState({ step: 'credentials' })}
            >
              Import more
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
